import React, { useContext, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Spin } from 'antd';
import { DatePicker, Input, EchartsWithTheme, useTheme, Empty } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import {
  apiV2RegionOverviewEnergyCostPost,
  apiV2RegionOverviewMediumUsagePost,
  apiV2RegionOverviewStructureRatioPost,
  apiV2RegionOverviewPeakValleyRatioPost,
  apiV2RegionOverviewCostTrendPost,
  apiV2RegionOverviewUsageTrendPost,
} from '@maxtropy/device-customer-apis-v2';
import titleIcon from '@/assets/images/title_icon.png';
import circleRotate from '../../imgs/circle-rotate@2x.png';
import circleImg from '../../imgs/circle@2x.png';
import costIcon from '../../imgs/cost@2x.png';
import gasIcon from '../../imgs/icon-gas@2x.png';
import powerIcon from '../../imgs/icon-power@2x.png';
import waterIcon from '../../imgs/icon-water@2x.png';
import usageSpinCircle from '../../../../imgs/usage_spin_circle.png';
import styles from './index.module.scss';
import { keepOneAndWan, keepTwoAndWanOrLine, keepPercentOrLine } from '@/utils/utils';
import { ShareDataUnityEnergyCockpitMapContext } from '../..';

// energyCards 数据由接口 /api/v2/region-overview/medium-usage 提供，见组件内 useMemo
// costData 数据由接口 /api/v2/region-overview/structure-ratio 提供，见组件内 useMemo

/** 成本饼图颜色 */
const COST_COLORS = ['#4addec', '#ff437d', '#ffd34c', '#7555ff', '#26d489', '#ff884c', '#58e8ff', '#7052ff'];
/** 获取成本饼图颜色 */
const getCostColor = (index: number) => COST_COLORS[index % COST_COLORS.length];
const TOOLTIP_WIDTH = 220;

// powerOverviewData 数据由接口 /api/v2/region-overview/peak-valley-ratio 提供，见组件内 useMemo

const getRateClassName = (value: string) => (value.startsWith('+') ? styles.up : styles.down);

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <div className={styles.sectionTitle}>
    <img src={titleIcon} alt="" />
    <span>{title}</span>
  </div>
);

const energyIconMap: Record<string, string> = {
  gas: gasIcon,
  power: powerIcon,
  water: waterIcon,
};

/** 介质名称 → icon 类型映射 */
const getMediumIconType = (mediumName?: string): string => {
  if (!mediumName) return 'gas';
  if (mediumName.includes('电')) return 'power';
  if (mediumName.includes('水')) return 'water';
  return 'gas';
};

const EnergyIcon: React.FC<{ type?: string }> = ({ type }) => (
  <div className={styles.energyIcon}>
    <img src={energyIconMap[type ?? 'gas'] ?? gasIcon} alt="" />
  </div>
);

const getProgressWidth = (value: string) => {
  const numericValue = Number.parseFloat(value);
  if (!Number.isFinite(numericValue)) return '0%';
  return `${Math.min(Math.max(numericValue, 0), 100)}%`;
};

const formatTooltipValue = (value: unknown, unit: string) => {
  if (value === undefined || value === null || value === '') return `--${unit}`;
  return `${value}${unit}`;
};

const getAxisTooltipFormatter = (getUnit: (param: any) => string) => (params: any) => {
  const list = Array.isArray(params) ? params : [params];
  const title = list[0]?.axisValueLabel ?? list[0]?.name ?? '';
  const rows = list
    .map(
      item => `
          <div style="display:flex;gap:12px;align-items:center;justify-content:space-between;">
            <span>${item.marker}${item.seriesName}</span>
            <span>${formatTooltipValue(item.value, getUnit(item))}</span>
          </div>
        `
    )
    .join('');
  return `<div style="width:${TOOLTIP_WIDTH}px;">${title ? `<div>${title}</div>` : ''}${rows}</div>`;
};

const ProgressBar: React.FC<{ value: string; showValue?: boolean }> = ({ value, showValue = true }) => (
  <div className={`${styles.progressWrap} ${!showValue ? styles.progressOnly : ''}`}>
    <div className={styles.progressTrack}>
      <div className={styles.progressBar} style={{ width: getProgressWidth(value) }} />
    </div>
    {showValue && <span>{value}</span>}
  </div>
);

const EnergyOverviewModal = () => {
  const theme = useTheme();
  const { dateValue, overviewRegionName } = useContext(ShareDataUnityEnergyCockpitMapContext);

  // 区域选择
  const [region, setRegion] = useState(overviewRegionName ?? '');
  const [inputRegion, setInputRegion] = useState(overviewRegionName ?? '');
  // 查询月份
  const [queryMonth, setQueryMonth] = useState<Dayjs>(dateValue || dayjs());

  useEffect(() => {
    setRegion(overviewRegionName ?? '');
    setInputRegion(overviewRegionName ?? '');
  }, [overviewRegionName]);

  // 调用 区域用能概览-能源费用 接口，获取 feature card 数据
  const { data: energyCostOverviewData, loading: loadingCost } = useRequest(
    async () => {
      const res = await apiV2RegionOverviewEnergyCostPost({
        regionName: region,
        filterMonth: queryMonth.format('YYYY-MM'),
      });
      return res;
    },
    {
      ready: !!queryMonth && !!region,
      refreshDeps: [queryMonth, region],
    }
  );

  // 调用 区域用能概览-介质用量 接口，获取 energyCards 数据
  const { data: mediumUsageData, loading: loadingMedium } = useRequest(
    async () => {
      const res = await apiV2RegionOverviewMediumUsagePost({
        regionName: region,
        filterMonth: queryMonth.format('YYYY-MM'),
      });
      return res;
    },
    {
      ready: !!queryMonth && !!region,
      refreshDeps: [queryMonth, region],
    }
  );

  // 调用 区域用能概览-成本结构占比 接口，获取 costData / 成本饼图数据
  const { data: structureRatioData, loading: loadingRatio } = useRequest(
    async () => {
      const res = await apiV2RegionOverviewStructureRatioPost({
        regionName: region,
        filterMonth: queryMonth.format('YYYY-MM'),
      });
      return res;
    },
    {
      ready: !!queryMonth && !!region,
      refreshDeps: [queryMonth, region],
    }
  );

  // 调用 区域用能概览-电峰谷占比 接口，获取 powerOverviewData 数据
  const { data: peakValleyRatioData, loading: loadingPeak } = useRequest(
    async () => {
      const res = await apiV2RegionOverviewPeakValleyRatioPost({
        regionName: region,
        filterMonth: queryMonth.format('YYYY-MM'),
      });
      return res;
    },
    {
      ready: !!queryMonth && !!region,
      refreshDeps: [queryMonth, region],
    }
  );

  // 调用 区域用能概览-费用趋势 接口，获取 costTrendOption 数据
  const { data: costTrendData, loading: loadingTrend } = useRequest(
    async () => {
      const res = await apiV2RegionOverviewCostTrendPost({
        regionName: region,
        filterMonth: queryMonth.format('YYYY-MM'),
      });
      return res;
    },
    {
      ready: !!queryMonth && !!region,
      refreshDeps: [queryMonth, region],
    }
  );

  // 调用 区域用能概览-用能趋势 接口，获取 energyTrendOption 数据
  const { data: usageTrendData, loading: loadingUsage } = useRequest(
    async () => {
      const res = await apiV2RegionOverviewUsageTrendPost({
        regionName: region,
        filterMonth: queryMonth.format('YYYY-MM'),
      });
      return res;
    },
    {
      ready: !!queryMonth && !!region,
      refreshDeps: [queryMonth, region],
    }
  );

  // 综合 loading 状态
  const isLoading = loadingCost || loadingMedium || loadingRatio || loadingPeak || loadingTrend || loadingUsage;

  // 从接口数据中提取 featured card（能源费用）数据
  const featuredCard = useMemo(() => {
    const data = energyCostOverviewData;
    const totalCost = data?.totalCost ?? 0;
    // yoy/mom 接口返回已是百分比数值（如 -2.12 表示 -2.12%）
    const mom = data?.mom;
    const yoy = data?.yoy;

    return {
      name: '能源费用(元)',
      value: totalCost,
      mom: mom !== undefined && mom !== null ? `${mom.toFixed(2)}%` : '--',
      yoy: yoy !== undefined && yoy !== null ? `${yoy.toFixed(2)}%` : '--',
    };
  }, [energyCostOverviewData]);

  // 从接口数据中提取 energyCards（介质用量卡片）数据
  const energyCards = useMemo(() => {
    const list = mediumUsageData?.mediumUsageList;
    if (!list || list.length === 0) return [];
    return list.map(item => {
      const mediumName = item.mediumName || '-';
      const formatted = keepOneAndWan(item.totalUsage);
      return {
        name: `${mediumName}用量(${item.unit || ''})`,
        value: `${formatted.value}${formatted.unit}`,
        mom: item.mom !== undefined && item.mom !== null ? `${item.mom.toFixed(2)}%` : '--',
        yoy: item.yoy !== undefined && item.yoy !== null ? `${item.yoy.toFixed(2)}%` : '--',
        plan:
          item.planCompletionRate !== undefined && item.planCompletionRate !== null
            ? `${item.planCompletionRate.toFixed(1)}%`
            : '--',
        icon: getMediumIconType(mediumName),
      };
    });
  }, [mediumUsageData]);

  // 从接口数据中提取 costData（能源成本占比）数据
  const costData = useMemo(() => {
    const list = structureRatioData?.mediumCostList;
    if (!list || list.length === 0) return [];
    return list.map((item, index) => ({
      name: item.mediumName || '-',
      percent: item.ratio != null ? Number(item.ratio).toFixed(2) : '0.00',
      costValue: item.cost ?? 0,
      costText: keepTwoAndWanOrLine(item.cost),
      value: `${keepTwoAndWanOrLine(item.cost)}元`,
      color: getCostColor(index),
    }));
  }, [structureRatioData]);

  // 成本占比最高/最低
  const costHighlight = useMemo(() => {
    const highest = structureRatioData?.highestRatio;
    const lowest = structureRatioData?.lowestRatio;
    return {
      highest: highest?.mediumName
        ? {
            name: highest.mediumName,
            percent: highest.ratio != null ? Number(highest.ratio).toFixed(2) : '0.00',
            value: `${keepTwoAndWanOrLine(highest.cost)}元`,
          }
        : null,
      lowest: lowest?.mediumName
        ? {
            name: lowest.mediumName,
            percent: lowest.ratio != null ? Number(lowest.ratio).toFixed(2) : '0.00',
            value: `${keepTwoAndWanOrLine(lowest.cost)}元`,
          }
        : null,
    };
  }, [structureRatioData]);

  // 从接口数据中提取 powerOverviewData（电峰谷占比）数据
  const powerOverviewData = useMemo(() => {
    const data = peakValleyRatioData;
    return {
      total: data?.totalElectricUsage ?? 0,
      summit: data?.summitUsage ?? 0,
      peak: data?.peakUsage ?? 0,
      plain: data?.plainUsage ?? 0,
      valley: data?.valleyUsage ?? 0,
      // 各时段占比，接口返回已是百分比数值
      summitRatio: data?.summitRatio ?? 0,
      peakRatio: data?.peakRatio ?? 0,
      plainRatio: data?.plainRatio ?? 0,
      valleyRatio: data?.valleyRatio ?? 0,
    };
  }, [peakValleyRatioData]);

  const costPieOption = useMemo(() => {
    const costDataMap = costData.reduce<Record<string, (typeof costData)[number]>>((prev, item) => {
      prev[item.name] = item;
      return prev;
    }, {});

    return {
      color: costData.map(item => item.color),
      legend: {
        show: true,
        type: 'scroll',
        orient: 'vertical',
        left: 260,
        right: 24,
        top: 84,
        bottom: 8,
        itemWidth: 8,
        itemHeight: 8,
        itemGap: 10,
        icon: 'circle',
        textStyle: {
          color: 'rgba(255, 255, 255, 0.65)',
          fontSize: 13,
          rich: {
            name: {
              width: 80,
              color: 'rgba(255, 255, 255, 0.65)',
            },
            percent: {
              width: 74,
              align: 'right',
              color: 'rgba(255, 255, 255, 0.65)',
            },
            value: {
              width: 140,
              align: 'right',
              color: 'rgba(255, 255, 255, 0.65)',
            },
          },
        },
        formatter: (name: string) => {
          const item = costDataMap[name];
          if (!item) return name;
          return `{name|${name}}{percent|${item.percent}%}{value|${item.value}}`;
        },
      },
      tooltip: {
        confine: true,
        formatter: (param: any) => `
          <div style="width:${TOOLTIP_WIDTH}px;">
            <div style="display:flex;gap:12px;align-items:center;">
              <span style="display:inline-block;margin-right:-10px">${param.marker}</span>
              <span>${param.name}</span>
              <span>${formatTooltipValue(param.data?.percent, '%')}</span>
              <span>${formatTooltipValue(param.data?.costText, '元')}</span>
            </div>
          </div>
        `,
      },
      graphic: {
        elements: [
          {
            type: 'image',
            left: 114,
            top: 82,
            style: {
              image: usageSpinCircle,
              x: -28,
              y: -28,
              width: 56,
              height: 56,
            },
            keyframeAnimation: [
              {
                duration: 300000,
                loop: true,
                easing: 'linear',
                keyframes: [
                  { percent: 0, rotation: 0, transition: ['rotation'] },
                  { percent: 1, rotation: 360, transition: ['rotation'] },
                ],
              },
            ],
          },
        ],
      },
      series: [
        {
          type: 'pie',
          radius: ['35%', '90%'],
          center: ['24%', '50%'],
          padAngle: 4,
          itemStyle: {
            borderRadius: 8,
          },
          avoidLabelOverlap: false,
          label: { show: false },
          minAngle: 4,
          showEmptyCircle: false,
          data: costData.map(item => ({
            name: item.name,
            value: item.costValue,
            percent: item.percent,
            costText: item.costText,
          })),
        },
      ],
    };
  }, [costData]);

  const chartPieOption = useMemo(() => {
    const chartData = [
      { name: '尖电量', value: powerOverviewData.summit, ratio: powerOverviewData.summitRatio.toFixed(2) },
      { name: '峰电量', value: powerOverviewData.peak, ratio: powerOverviewData.peakRatio.toFixed(2) },
      { name: '平电量', value: powerOverviewData.plain, ratio: powerOverviewData.plainRatio.toFixed(2) },
      { name: '谷电量', value: powerOverviewData.valley, ratio: powerOverviewData.valleyRatio.toFixed(2) },
    ];
    return {
      color: ['#FF6D2C', '#CE90D1', '#0DB6D9', '#63BC7F'],
      tooltip: {
        confine: true,
        formatter: (param: any) => `
          <div style="width:${TOOLTIP_WIDTH}px;">
            <div style="display:flex;gap:12px;align-items:center;">
              <span style="display:inline-block;margin-right:-10px">${param.marker}</span>
              <span>${param.name}</span>
              <span>${formatTooltipValue(param.value, 'kWh')}</span>
              <span>${formatTooltipValue(param.data?.ratio, '%')}</span>
            </div>
          </div>
        `,
      },
      title: {
        text: '用电总量(kWh)',
        textStyle: {
          fontSize: 12,
          color: 'rgba(255,255,255,0.65)',
        },
        subtext: `{normal|${keepOneAndWan(powerOverviewData.total).value}}{small|${
          keepOneAndWan(powerOverviewData.total).unit
        }}`,
        subtextStyle: {
          rich: {
            normal: {
              fontSize: 20,
              color: '#FFF',
              padding: [0, 4, 0, 0],
            },
            small: {
              fontSize: 16,
              color: '#FFF',
            },
          },
        },
        left: 94,
        top: 78,
        itemGap: 8,
        textAlign: 'center',
      },
      series: [
        {
          type: 'pie',
          name: '电量(kW)',
          percentPrecision: 5,
          minAngle: 2,
          padAngle: 1,
          radius: [66, 78],
          left: 35,
          top: 35,
          width: 130,
          height: 130,
          label: { show: false },
          data: chartData,
          showEmptyCircle: false,
        },
        {
          showEmptyCircle: false,
          data: chartData,
          type: 'pie',
          percentPrecision: 5,
          padAngle: 1,
          minAngle: 2,
          name: '电量(kW)',
          radius: [76, 92],
          left: 38,
          top: 38,
          width: 124,
          height: 124,
          label: { show: false },
          itemStyle: {
            opacity: 0.2,
          },
        },
      ],
    };
  }, [powerOverviewData]);

  // 能源费用趋势数据
  const costTrendLabels = useMemo(() => {
    return costTrendData?.dailyCostList?.map(item => dayjs(item.timestamp).format('MM-DD')) || [];
  }, [costTrendData]);

  const costTrendActual = useMemo(() => {
    return costTrendData?.dailyCostList?.map(item => item.cost ?? null) || [];
  }, [costTrendData]);

  const costTrendLastYear = useMemo(() => {
    return costTrendData?.dailyCostList?.map(item => item.lastYearCost ?? null) || [];
  }, [costTrendData]);

  const costTrendOption = useMemo(
    () => ({
      backgroundColor: 'transparent',
      color: ['#16DD8E', '#FFCB47'],
      grid: { top: 36, left: 16, right: 20, bottom: 16 },
      legend: {
        right: 12,
        top: 0,
        itemWidth: 16,
        itemHeight: 4,
        itemGap: 22,
        data: [
          {
            name: '本区域实际',
            icon: 'path://M0 426.666667h1024v170.666666H0z',
          },
          {
            name: '去年同期',
            icon: 'path://M0 426.666667h1024v170.666666H0z',
          },
        ],
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: getAxisTooltipFormatter(() => '元'),
      },
      xAxis: {
        type: 'category',
        data: costTrendLabels,
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        name: '元',
        min: 0,
      },
      series: [
        {
          name: '本区域实际',
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 1, color: '#16DD8E' },
          data: costTrendActual,
        },
        {
          name: '去年同期',
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 1, color: '#FFCB47' },
          data: costTrendLastYear,
        },
      ],
    }),
    [costTrendLabels, costTrendActual, costTrendLastYear]
  );

  // 用能趋势颜色
  const ELECTRICITY_TREND_COLOR = '#52E7FF';
  const ENERGY_TREND_COLORS = ['#2D8DFF', '#FF477B', '#FFCB47', '#6F47FF', '#16DD8E', '#ABD335'];

  // 用能趋势 x 轴标签
  const usageTrendLabels = useMemo(() => {
    return usageTrendData?.dailyUsageList?.map(item => dayjs(item.timestamp).format('MM-DD')) || [];
  }, [usageTrendData]);

  const energyTrendOption = useMemo(() => {
    const dailyList = usageTrendData?.dailyUsageList || [];
    const mediumList = usageTrendData?.mediumList || [];

    if (mediumList.length === 0) {
      return {};
    }

    const orderedMediumList = [...mediumList].sort((a, b) => {
      const aIsElectricity = a.mediumName?.includes('电') ? 1 : 0;
      const bIsElectricity = b.mediumName?.includes('电') ? 1 : 0;
      return bIsElectricity - aIsElectricity;
    });
    const hasElectricity = orderedMediumList.some(item => item.mediumName?.includes('电'));
    const hasOtherMedium = orderedMediumList.some(item => !item.mediumName?.includes('电'));

    // 为每个介质构建按日数据数组
    const legendData: { name: string; icon: string }[] = [];
    const series: any[] = [];
    const mediumUnitMap: Record<string, string> = {};
    const colors: string[] = [];
    let nonElectricityColorIndex = 0;

    orderedMediumList.forEach(medium => {
      const mediumName = medium.mediumName || '';
      const isElectricity = mediumName.includes('电');
      const isWater = mediumName.includes('自来水') || mediumName.includes('水');
      mediumUnitMap[mediumName] = isElectricity ? 'kWh' : (medium as any).unit || 'm³';
      colors.push(
        isElectricity
          ? ELECTRICITY_TREND_COLOR
          : ENERGY_TREND_COLORS[nonElectricityColorIndex++ % ENERGY_TREND_COLORS.length]
      );

      // 该介质每日用量
      const data = dailyList.map(day => {
        const usage = day.mediumUsageList?.find(m => m.mediumId === medium.mediumId);
        return usage?.usage ?? null;
      });

      // 图例 icon
      const legendIcon = isElectricity
        ? 'rect'
        : isWater
        ? 'path://M0 426.666667h220v170.666666H0zM320 426.666667h220v170.666666H320zM640 426.666667h220v170.666666H640z'
        : 'path://M0 426.666667h1024v170.666666H0z';

      legendData.push({ name: mediumName, icon: legendIcon });

      // yAxisIndex: 电用 kWh；电和其他介质同时存在时，其他介质用右轴，否则单轴放左侧。
      const yAxisIndex = isElectricity || !hasElectricity ? 0 : 1;

      // 线类型: 自来水用虚线
      const lineStyle: any = { width: 1 };
      if (isWater && !isElectricity) {
        lineStyle.type = 'dashed';
      }

      series.push({
        name: mediumName,
        type: isElectricity ? 'bar' : 'line',
        barWidth: isElectricity ? 5 : undefined,
        smooth: !isElectricity,
        yAxisIndex,
        symbol: 'none',
        lineStyle,
        data,
      });
    });

    return {
      backgroundColor: 'transparent',
      color: colors,
      grid: { top: 56, left: 16, right: 42, bottom: 16 },
      legend: {
        right: 8,
        top: 0,
        itemWidth: 12,
        itemHeight: 12,
        itemGap: 18,
        data: legendData,
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: getAxisTooltipFormatter(param => mediumUnitMap[param.seriesName] ?? 'm³'),
      },
      xAxis: {
        type: 'category',
        data: usageTrendLabels,
        axisTick: { show: false },
      },
      yAxis: [
        {
          type: 'value',
          name: hasElectricity ? 'kWh' : hasOtherMedium ? 'm³' : '',
          show: hasElectricity || hasOtherMedium,
          position: 'left',
        },
        {
          type: 'value',
          name: hasOtherMedium ? 'm³' : '',
          show: hasElectricity && hasOtherMedium,
          position: 'right',
          splitLine: { show: false },
        },
      ],
      series,
    };
  }, [usageTrendData, usageTrendLabels]);

  return (
    <div className={styles.energyOverviewModal}>
      <Spin spinning={isLoading}>
        <div className={styles.filters}>
          <Input className={styles.select} placeholder="请输入区域名称" value={inputRegion} disabled />
          <DatePicker
            picker="month"
            value={queryMonth}
            className={styles.datePicker}
            allowClear={false}
            disabledDate={current => !!current && current.isAfter(dayjs(), 'month')}
            onChange={v => v && setQueryMonth(v)}
          />
        </div>

        <div className={styles.cardGrid}>
          <div className={styles.featuredCard}>
            <div className={styles.featuredIconWrap}>
              <div className={styles.costIconWrap}>
                <img className={styles.costIconCircle} src={circleRotate} alt="" />
                <img className={styles.costIconImg} src={costIcon} alt="" />
              </div>
              <div>
                <div className={styles.cardTitle}>{featuredCard.name}:</div>
                <div className={styles.cardValue}>
                  {keepOneAndWan(featuredCard.value).value}
                  <span style={{ fontSize: 14 }}>{keepOneAndWan(featuredCard.value).unit}</span>
                </div>
              </div>
            </div>

            <div className={styles.featuredDivider} />
            <div className={styles.rateRow}>
              <span>
                环比：<b className={getRateClassName(featuredCard.mom)}>{featuredCard.mom}</b>
              </span>
              <span>
                同比：<b className={getRateClassName(featuredCard.yoy)}>{featuredCard.yoy}</b>
              </span>
            </div>
          </div>
          <div className={styles.cardStack}>
            {energyCards.length === 0 ? (
              <div className={styles.energyCardsEmpty}>
                <Empty />
              </div>
            ) : (
              energyCards.map(card => (
                <div key={card.name} className={styles.energyCard}>
                  <EnergyIcon type={card.icon} />
                  <div className={styles.cardMain}>
                    <div className={styles.energyCardHeader}>
                      <div className={styles.cardTitle}>{card.name}:</div>
                      <div className={styles.cardValue}>{card.value}</div>
                    </div>
                    <div className={styles.energyDivider} />
                    <div className={styles.rateRow}>
                      <span>
                        环比：<b className={getRateClassName(card.mom)}>{card.mom}</b>
                      </span>
                      <span>
                        同比：<b className={getRateClassName(card.yoy)}>{card.yoy}</b>
                      </span>
                    </div>
                    <div className={styles.planRow}>
                      <span>实际/计划:</span>
                      <ProgressBar value={card.plan} />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={styles.twoColumns}>
          <div className={styles.panel}>
            <SectionTitle title="能源成本占比" />
            {costData.length === 0 ? (
              <Empty style={{ paddingTop: 30 }} />
            ) : (
              <div className={styles.costArea}>
                <EchartsWithTheme
                  option={costPieOption}
                  theme={theme}
                  type="pie"
                  style={{ width: '100%', height: 220 }}
                />
                {(costHighlight.highest || costHighlight.lowest) && (
                  <div className={styles.costHighlightList}>
                    {costHighlight.highest && (
                      <div className={styles.costHighlight}>
                        最高占比：{costHighlight.highest.name} ({costHighlight.highest.percent}%)
                        {costHighlight.highest.value}
                      </div>
                    )}
                    {costHighlight.lowest && (
                      <div className={styles.costHighlight}>
                        最低占比：{costHighlight.lowest.name} ({costHighlight.lowest.percent}%)
                        {costHighlight.lowest.value}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.panel}>
            <SectionTitle title="电峰谷占比" />
            <div className={styles.box}>
              {/* 谷电量 */}
              <div className={styles.topLeftHorn}>
                <div className={styles.label}>谷电量(kWh)</div>
                <div className={styles.power}>
                  {keepOneAndWan(powerOverviewData.valley).value}
                  <span className={styles.unit}>{keepOneAndWan(powerOverviewData.valley).unit}</span>
                </div>
                <div className={styles.rate}>{powerOverviewData.valleyRatio.toFixed(2)}%</div>
              </div>
              {/* 尖电量 */}
              <div className={styles.topRightHorn}>
                <div className={styles.label}>尖电量(kWh)</div>
                <div className={styles.power}>
                  {keepOneAndWan(powerOverviewData.summit).value}
                  <span className={styles.unit}>{keepOneAndWan(powerOverviewData.summit).unit}</span>
                </div>
                <div className={styles.rate}>{powerOverviewData.summitRatio.toFixed(2)}%</div>
              </div>
              {/* 平电量 */}
              <div className={styles.bottomLeftHorn}>
                <div className={styles.label}>平电量(kWh)</div>
                <div className={styles.power}>
                  {keepOneAndWan(powerOverviewData.plain).value}
                  <span className={styles.unit}>{keepOneAndWan(powerOverviewData.plain).unit}</span>
                </div>
                <div className={styles.rate}>{powerOverviewData.plainRatio.toFixed(2)}%</div>
              </div>
              {/* 峰电量 */}
              <div className={styles.bottomRightHorn}>
                <div className={styles.label}>峰电量(kWh)</div>
                <div className={styles.power}>
                  {keepOneAndWan(powerOverviewData.peak).value}
                  <span className={styles.unit}>{keepOneAndWan(powerOverviewData.peak).unit}</span>
                </div>
                <div className={styles.rate}>{powerOverviewData.peakRatio.toFixed(2)}%</div>
              </div>
              {/* 饼图 */}
              <div className={styles.circle}>
                <EchartsWithTheme
                  theme={theme}
                  type="pie"
                  style={{ height: 200, width: 200 }}
                  option={chartPieOption}
                />
                <img src={circleImg} alt="" className={styles.rotateCircle} />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.twoColumns}>
          <div className={styles.panel}>
            <SectionTitle title="能源费用趋势" />
            {costTrendLabels.length === 0 ? (
              <Empty style={{ padding: '30px 0 50px' }} />
            ) : (
              <>
                <div className={styles.trendSummary}>
                  <div className={styles.real}>
                    <span>本期实际：</span>
                    <span>
                      {keepOneAndWan(costTrendData?.totalCost).value}
                      {keepOneAndWan(costTrendData?.totalCost).unit}元
                    </span>
                  </div>
                  <div className={styles.last}>
                    <span>去年同期：</span>
                    <span>
                      {keepOneAndWan(costTrendData?.lastYearTotalCost).value}
                      {keepOneAndWan(costTrendData?.lastYearTotalCost).unit}元
                    </span>
                  </div>
                  <div className={styles.percent}>
                    <span>同比：</span>
                    <span>{keepPercentOrLine(costTrendData?.yoy)}</span>
                  </div>
                </div>
                <EchartsWithTheme option={costTrendOption} theme={theme} style={{ width: '100%', height: 204 }} />
              </>
            )}
          </div>

          <div className={styles.panel}>
            <SectionTitle title="用能趋势" />
            {usageTrendLabels.length === 0 ? (
              <Empty style={{ padding: '30px 0 50px' }} />
            ) : (
              <EchartsWithTheme option={energyTrendOption} theme={theme} style={{ width: '100%', height: 254 }} />
            )}
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default EnergyOverviewModal;
